/**
 * IncidentsDashboard — Ecosystem-wide behaviour/incident dashboard.
 *
 * Shows: urgency tiles (yellow/amber/red), red-alert queue, repeat
 * offenders, by-category breakdown, recent incidents list with status
 * controls. Filterable by context_type (all/school/event/church/...).
 *
 * Mounted at:  /admin/incidents
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertTriangle, Shield, Loader2, ArrowLeft, Scan, RefreshCw, Repeat, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import IncidentReporter from '@/components/incidents/IncidentReporter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const URGENCY_STYLE = {
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', label: 'Yellow' },
  amber:  { bg: 'bg-amber-100',  text: 'text-amber-700',  border: 'border-amber-300',  label: 'Amber'  },
  red:    { bg: 'bg-rose-100',   text: 'text-rose-700',   border: 'border-rose-300',   label: 'Red'    },
};

const STATUS_STYLE = {
  submitted:    'bg-slate-100 text-slate-700',
  under_review: 'bg-blue-100 text-blue-700',
  action_taken: 'bg-indigo-100 text-indigo-700',
  escalated:    'bg-rose-100 text-rose-700',
  resolved:     'bg-emerald-100 text-emerald-700',
  closed:       'bg-slate-200 text-slate-600',
};

const CONTEXTS = ['all', 'school', 'event', 'church', 'camp', 'conference', 'security', 'accommodation', 'dining', 'transport'];

const fetchJson = async (path) => {
  const res = await fetch(`${API_URL}${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Request failed (${res.status})`);
  return res.json();
};

export default function IncidentsDashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [context, setContext] = useState('all');
  const [reporterOpen, setReporterOpen] = useState(false);

  const filterParam = context === 'all' ? '' : `?context_type=${context}`;

  const { data: dash, isLoading: loadingDash } = useQuery({
    queryKey: ['incidents-dashboard', context],
    queryFn: () => fetchJson(`/api/incidents/dashboard${filterParam}`),
  });
  const { data: list, isLoading: loadingList } = useQuery({
    queryKey: ['incidents-list', context],
    queryFn: () => fetchJson(`/api/incidents${filterParam}${filterParam ? '&' : '?'}limit=50`),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }) =>
      fetch(`${API_URL}/api/incidents/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status }),
      }).then(r => r.json()),
    onSuccess: () => {
      toast.success('Status updated');
      qc.invalidateQueries({ queryKey: ['incidents-list'] });
      qc.invalidateQueries({ queryKey: ['incidents-dashboard'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['incidents-list'] });
    qc.invalidateQueries({ queryKey: ['incidents-dashboard'] });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="incidents-dashboard">
      <header className="bg-gradient-to-r from-slate-900 via-indigo-900 to-violet-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-white/10" data-testid="incidents-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Shield className="h-5 w-5" /> Behaviour &amp; Incidents
            </h1>
            <p className="text-xs text-white/70">Ecosystem-wide reporting · School · Events · Church · Camps · Security</p>
          </div>
          <Button variant="ghost" size="icon" onClick={refresh} className="text-white hover:bg-white/10" data-testid="incidents-refresh">
            <RefreshCw className="h-5 w-5" />
          </Button>
          <Button onClick={() => setReporterOpen(true)} className="bg-white text-indigo-900 hover:bg-white/90" data-testid="incidents-scan-report">
            <Scan className="h-4 w-4 mr-1" /> Scan &amp; report
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Context filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <Building2 className="h-4 w-4 text-slate-500" />
          <span className="text-xs uppercase tracking-wider text-slate-500">Context:</span>
          <Select value={context} onValueChange={setContext}>
            <SelectTrigger className="w-44" data-testid="incidents-context-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONTEXTS.map(c => <SelectItem key={c} value={c}>{c === 'all' ? 'All contexts' : c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Urgency tiles */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="incidents-stats">
          {loadingDash ? (
            <Card className="col-span-full"><CardContent className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</CardContent></Card>
          ) : (
            <>
              {Object.entries(URGENCY_STYLE).map(([k, s]) => (
                <Card key={k} className={`${s.border} border-2`}>
                  <CardContent className="p-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.label} urgency</div>
                    <div className={`text-3xl font-bold ${s.text}`} data-testid={`stat-${k}`}>
                      {dash?.by_urgency?.[k] ?? 0}
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Card className="border-rose-300 border-2 bg-rose-50/50">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-rose-700 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Open red alerts
                  </div>
                  <div className="text-3xl font-bold text-rose-700" data-testid="stat-red-alerts">
                    {dash?.red_alerts ?? 0}
                  </div>
                  <div className="text-[10px] text-slate-600 mt-1">{dash?.unresolved ?? 0} total unresolved</div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Two columns: repeat offenders + by category */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Repeat className="h-4 w-4" /> Repeat offenders
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(dash?.repeat_offenders || []).length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-400">No repeat offenders 🎉</div>
              ) : (
                <ul className="divide-y" data-testid="repeat-offenders-list">
                  {dash.repeat_offenders.map((r, i) => (
                    <li key={r.person_id || i} className="flex items-center justify-between p-3">
                      <span className="text-sm font-medium">{r.name || r.person_id}</span>
                      <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">
                        {r.count} incidents
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">By category</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(dash?.by_category || {}).length === 0 ? (
                <p className="text-sm text-slate-400 text-center">No data yet.</p>
              ) : (
                Object.entries(dash.by_category).slice(0, 8).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <span className="capitalize text-sm">{k.replace(/_/g, ' ')}</span>
                    <Badge>{v}</Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent incidents */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Shield className="h-4 w-4" /> Recent incidents
              {list?.count > 0 && <Badge variant="outline">{list.count}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {loadingList ? (
              <div className="p-6 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>
            ) : (list?.incidents || []).length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">No incidents recorded.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 border-b">
                  <tr>
                    <th className="text-left p-3">Person</th>
                    <th className="text-left p-3">Category</th>
                    <th className="text-left p-3">Urgency</th>
                    <th className="text-left p-3">Context</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Reporter</th>
                    <th className="text-left p-3">When</th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {list.incidents.map((i) => {
                    const us = URGENCY_STYLE[i.urgency] || URGENCY_STYLE.yellow;
                    return (
                      <tr key={i.id} className="border-b hover:bg-slate-50" data-testid={`incident-row-${i.id}`}>
                        <td className="p-3">
                          <div className="font-medium">{i.person_name || i.person_id}</div>
                          <div className="text-xs text-slate-500 capitalize">{i.person_kind}</div>
                        </td>
                        <td className="p-3 capitalize text-xs">{(i.category || '').replace(/_/g, ' ')}</td>
                        <td className="p-3">
                          <Badge variant="outline" className={`${us.bg} ${us.text} ${us.border}`}>{us.label}</Badge>
                        </td>
                        <td className="p-3 text-xs capitalize">{i.context_type || '—'}</td>
                        <td className="p-3">
                          <Badge variant="outline" className={STATUS_STYLE[i.status] || STATUS_STYLE.submitted}>
                            {(i.status || 'submitted').replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="p-3 text-xs">{i.reported_by_name || i.reported_by_email || '—'}</td>
                        <td className="p-3 text-xs text-slate-500">
                          {i.created_at ? new Date(i.created_at).toLocaleString() : ''}
                        </td>
                        <td className="p-3">
                          {i.status !== 'resolved' && i.status !== 'closed' && (
                            <Select
                              value={i.status}
                              onValueChange={(s) => updateMutation.mutate({ id: i.id, status: s })}
                            >
                              <SelectTrigger className="h-7 w-32 text-xs" data-testid={`status-select-${i.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="submitted">Submitted</SelectItem>
                                <SelectItem value="under_review">Under review</SelectItem>
                                <SelectItem value="action_taken">Action taken</SelectItem>
                                <SelectItem value="escalated">Escalated</SelectItem>
                                <SelectItem value="resolved">Resolved</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </main>

      <IncidentReporter
        open={reporterOpen}
        onOpenChange={setReporterOpen}
        contextType={context === 'all' ? 'school' : context}
        onCreated={refresh}
      />
    </div>
  );
}
